import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { ActivityState } from '@/api/models/activity'
import { LearnerObjectiveAccessCompactModel, LearnerObjectiveAccessFullModel } from '@/api/models/aggregate'
import { useAppSelector } from '@/store/hooks'
import { useIsMigratedCourse } from '@/views/courses/CourseInfo/components/EnrolledCourseButtons/useIsMigratedCourse'
import { useGradingVisibility } from '@/views/courses/hooks/useGradingVisibility'
import { useIsNotAvailable } from '@/views/courses/hooks/useIsNotAvailable'

export const useIsObjectiveNotAvailable = (aggregate?: LearnerObjectiveAccessFullModel) => {
  const location = useLocation()
  const params = useParams()
  const aggregateCurrent = useAppSelector((store) => store.aggregate.current)
  const objectiveRecords = useAppSelector((store) => store.aggregate.records)
  const { isNotAvailableMessage, canReview } = useIsNotAvailable(aggregate)
  const isEdxMigratedCourse = useIsMigratedCourse(objectiveRecords)
  const { isCompletedBeforeGradeReveal, isGradeRevealNeverShow, isFinished } = useGradingVisibility(
    aggregate as LearnerObjectiveAccessCompactModel,
  )

  const aggregateId = aggregateCurrent?.id || params?.aggregateIdParam
  const isCourseDetail = location.pathname.includes('/courses') && aggregateId

  const isObjectiveNotAvailable = useMemo(() => {
    if (isCourseDetail) {
      const objectiveState = aggregate?.objective?.activity?.state
      const isDraftOrArchived = objectiveState && [ActivityState.draft, ActivityState.archived].includes(objectiveState)
      const isNotAvailable = Boolean(
        isNotAvailableMessage || isDraftOrArchived || !canReview || isEdxMigratedCourse || isCompletedBeforeGradeReveal,
      )

      return isNotAvailable
    } else {
      const supportReview = !!aggregate?.objective?.activity?.supportReview
      const isCourseNotAvailableAfterCompletion = isFinished && (isGradeRevealNeverShow || !supportReview)
      return isCourseNotAvailableAfterCompletion
    }
  }, [
    aggregate?.objective?.activity?.state,
    aggregate?.objective?.activity?.supportReview,
    canReview,
    isCompletedBeforeGradeReveal,
    isCourseDetail,
    isEdxMigratedCourse,
    isFinished,
    isGradeRevealNeverShow,
    isNotAvailableMessage,
  ])

  return { isObjectiveNotAvailable }
}
